import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdAdd,
  MdArticle,
  MdFilterListOff,
  MdRefresh,
  MdSync,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { ResponsiveDataList } from '@components/ui/ResponsiveDataList';
import { TabFilter } from '@components/ui/TabFilter';
import { NewsArticleCard } from '@features/newsArticles/components/NewsArticleCard';
import { NewsArticlesGrid } from '@features/newsArticles/components/NewsArticlesGrid';
import { NewsArticleFormDialog } from '@features/newsArticles/components/NewsArticleFormDialog';
import {
  useDeleteNewsArticle,
  useNewsArticles,
} from '@features/newsArticles/hooks';
import { useCacheSync } from '@features/cache/hooks';
import {
  NEWS_ARTICLE_KINDS,
  type ListNewsArticlesParams,
  type NewsArticle,
  type NewsArticleSortKey,
  type SortDirection,
} from '@features/newsArticles/schema';

const DEFAULT_PAGE_SIZE = 50;

type StatusTabValue = '' | NonNullable<ListNewsArticlesParams['status']>;

const STATUS_TABS: ReadonlyArray<{ value: StatusTabValue; label: string }> = [
  { value: '', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

export default function NewsArticlesScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [status, setStatus] = useState<StatusTabValue>('');
  const [kind, setKind] = useState<ListNewsArticlesParams['kind'] | ''>('');
  const [sort, setSort] = useState<{
    sortBy?: NewsArticleSortKey;
    sortDir?: SortDirection;
  }>({});

  // Debounce search input so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever filters or sort change.
  useEffect(() => {
    setPage(1);
  }, [searchText, status, kind, pageSize, sort]);

  const params = useMemo<ListNewsArticlesParams>(
    () => ({
      page,
      pageSize,
      searchText: searchText || undefined,
      status: status || undefined,
      kind: kind || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, searchText, status, kind, sort],
  );

  const { data, isLoading, isFetching, refetch } = useNewsArticles(params);
  const deleteMutation = useDeleteNewsArticle();
  const cacheSyncMutation = useCacheSync('news-article');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NewsArticle | null>(null);
  const [deleting, setDeleting] = useState<NewsArticle | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: NewsArticle) => {
    setEditing(row);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleDelete = (row: NewsArticle) => {
    setDeleting(row);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id, {
      onSuccess: () => setDeleting(null),
      onError: () => setDeleting(null),
    });
  };

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters = Boolean(searchText || status || kind);
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setStatus('');
    setKind('');
  };

  return (
    <Page
      title="News articles"
      subtitle="Manage published, draft, and archived items."
      fullHeight
      headerRightSlot={
        <div className="flex items-center gap-3">
          <Button
            variant="contained"
            size="small"
            startIcon={cacheSyncMutation.isPending ? undefined : <MdSync />}
            onClick={() => cacheSyncMutation.mutate()}
            disabled={cacheSyncMutation.isPending}
            sx={{
              minWidth: '120px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {cacheSyncMutation.isPending ? (
              <>
                <CircularProgress size={14} sx={{ mr: 0.75 }} />
                Syncing…
              </>
            ) : (
              'Sync'
            )}
          </Button>
          <TabFilter
            options={STATUS_TABS}
            current={status}
            onChange={setStatus}
            ariaLabel="Filter by status"
          />
        </div>
      }
      topBarAction={
        <Button
          variant="contained"
          size="small"
          startIcon={<MdAdd />}
          onClick={openCreate}
        >
          New
        </Button>
      }
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Search title or slug"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full md:min-w-[220px] md:flex-1"
        />
        <TextField
          label="Kind"
          size="small"
          select
          value={kind}
          onChange={(e) => setKind(e.target.value as ListNewsArticlesParams['kind'] | '')}
          className="w-full md:w-[140px]"
        >
          <MenuItem value="">All</MenuItem>
          {NEWS_ARTICLE_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {k.charAt(0).toUpperCase() + k.slice(1)}
            </MenuItem>
          ))}
        </TextField>
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[110px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {/* Refetch indicator: visible during filter changes / pagination /
            manual refresh. The grid keeps showing previous data underneath
            (placeholderData) so the user has spatial context. */}
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '2fr', bar: 'w-3/4' },
              { width: '1fr', bar: 'w-2/3' },
              { width: '120px', bar: 'w-14' },
              { width: '140px', bar: 'w-20' },
              { width: '200px', bar: 'w-24' },
              { width: '130px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="Nothing matches the current filters. Try widening your search."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdArticle}
              title="No news articles yet"
              message="Publish your first article to populate this workspace."
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MdAdd />}
                  onClick={openCreate}
                >
                  New article
                </Button>
              }
            />
          )
        ) : (
          <ResponsiveDataList
            rows={data?.rowData ?? []}
            getRowId={(r) => r.id}
            desktop={
              <NewsArticlesGrid
                data={data}
                onEdit={openEdit}
                onDelete={handleDelete}
                onSortChange={setSort}
              />
            }
            renderCard={(row) => (
              <NewsArticleCard
                row={row}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            )}
          />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <NewsArticleFormDialog open={dialogOpen} article={editing} onClose={closeDialog} />

      <Dialog
        open={!!deleting}
        onClose={() => !deleteMutation.isPending && setDeleting(null)}
        aria-labelledby="confirm-delete-article-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirm-delete-article-title" sx={{ pb: 1 }}>
          Delete this article?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleting ? (
              <>
                <strong className="text-foreground">{deleting.title}</strong> will
                be soft-deleted. The row can be restored from the database, but it
                will disappear from this list and the mobile app immediately.
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleting(null)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            autoFocus
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

